export type ColorSchemePreference = 'light' | 'dark' | 'system';

export const DEFAULT_COLOR_SCHEME: ColorSchemePreference = 'light';

const STORAGE_KEY = 'meister.colorScheme';
const CHANGE_EVENT = 'meister:color-scheme-change';

let preference: ColorSchemePreference = readStoredPreference();
let userChangeEpoch = 0;
let mediaQuery: MediaQueryList | null = null;
let hydratedUserId: string | null = null;

export function isColorSchemePreference(value: unknown): value is ColorSchemePreference {
  return value === 'light' || value === 'dark' || value === 'system';
}

export function resolvesToDark(
  value: ColorSchemePreference,
  prefersDark: boolean,
): boolean {
  return value === 'dark' || (value === 'system' && prefersDark);
}

function readStoredPreference(): ColorSchemePreference {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return isColorSchemePreference(stored) ? stored : DEFAULT_COLOR_SCHEME;
  } catch {
    return DEFAULT_COLOR_SCHEME;
  }
}

function systemPrefersDark(): boolean {
  return typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches;
}

function applyPreference(next: ColorSchemePreference): void {
  const isDark = resolvesToDark(next, systemPrefersDark());
  document.documentElement.classList.toggle('dark', isDark);
  document.documentElement.style.colorScheme = isDark ? 'dark' : 'light';
}

function emitChange(): void {
  window.dispatchEvent(new CustomEvent<ColorSchemePreference>(CHANGE_EVENT, { detail: preference }));
}

function persistLocally(next: ColorSchemePreference): void {
  try {
    localStorage.setItem(STORAGE_KEY, next);
  } catch {
    // The in-memory and DOM state still work when storage is unavailable.
  }
}

export function getColorSchemePreference(): ColorSchemePreference {
  return preference;
}

export function initializeColorSchemePreference(): () => void {
  preference = readStoredPreference();
  applyPreference(preference);
  mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
  const onSystemChange = () => {
    if (preference === 'system') applyPreference('system');
  };
  mediaQuery.addEventListener('change', onSystemChange);
  return () => mediaQuery?.removeEventListener('change', onSystemChange);
}

export function subscribeColorSchemePreference(
  listener: (next: ColorSchemePreference) => void,
): () => void {
  const onChange = (event: Event) => {
    listener((event as CustomEvent<ColorSchemePreference>).detail);
  };
  window.addEventListener(CHANGE_EVENT, onChange);
  return () => window.removeEventListener(CHANGE_EVENT, onChange);
}

export async function setColorSchemePreference(
  next: ColorSchemePreference,
  options?: { syncToServer?: boolean },
): Promise<void> {
  preference = next;
  userChangeEpoch += 1;
  persistLocally(next);
  applyPreference(next);
  emitChange();

  if (options?.syncToServer === false) return;
  const response = await fetch('/api/me/preferences/ui', {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ colorScheme: next }),
  });
  if (!response.ok) {
    const data = await response.json().catch(() => null);
    throw new Error(data?.error || 'Farbschema konnte nicht gespeichert werden.');
  }
}

export async function hydrateColorSchemePreference(userId?: string): Promise<void> {
  if (userId && hydratedUserId === userId) return;
  const epochAtStart = userChangeEpoch;
  const response = await fetch('/api/me/preferences');
  if (!response.ok) return;
  const data = await response.json().catch(() => null);
  if (epochAtStart !== userChangeEpoch || !isColorSchemePreference(data?.colorScheme)) return;

  preference = data.colorScheme;
  persistLocally(preference);
  applyPreference(preference);
  emitChange();
  if (userId) hydratedUserId = userId;
}
